import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { CONTACT, usePageTitle } from "@/lib/site";

const SECTIONS = [
  {
    title: "What we collect",
    body: [
      "When you submit the consultation or booking form on this site, we collect the details you provide: your name, email address, phone number, organization, the type of organization you represent, the service you are interested in, and your message or appointment preferences.",
      "If you book through our scheduling calendar or use the client portal, those services (Calendly and Financial Cents) collect information under their own privacy policies.",
      "We also keep privacy-friendly tallies of which pages are visited and where visitors arrive from. These counts live on our own systems, use no cookies or advertising identifiers, and are never tied to your identity.",
    ],
  },
  {
    title: "How we use it",
    body: [
      "We use the information you send us for one purpose: to respond to your enquiry, schedule and prepare for appointments, and — if you become a client — to deliver our services. That includes sending you a confirmation email when you submit a form.",
    ],
  },
  {
    title: "What we never do",
    body: [
      "We do not sell, rent, or trade your personal information. This site uses the Google Ads tag to measure whether our advertising leads to enquiries — that involves Google's cookies, handled under Google's own privacy policy. Our own visitor tallies remain cookie-free and first-party.",
    ],
  },
  {
    title: "Who processes your information",
    body: [
      "Form submissions are stored in our secured site database and delivered to our team by email. Scheduling is handled by Calendly, and the client portal is provided by Financial Cents. Each of these providers processes data only as needed to deliver their service to us.",
    ],
  },
  {
    title: "Retention and security",
    body: [
      "We keep enquiry records only as long as needed to handle your request and meet professional obligations, and we restrict access to the people who need it. Administrative access to submission records is password-protected.",
    ],
  },
  {
    title: "Your choices",
    body: [
      "You may ask us at any time to correct or delete the information you have sent us, or to answer any question about how it is handled. Just reach out using the details below.",
    ],
  },
];

export default function Privacy() {
  usePageTitle("Privacy Policy — CalCor Solutions");

  return (
    <>
      <section data-testid="privacy-page" className="pt-36 pb-24 sm:pt-44 lg:pb-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <FadeUp>
            <Link
              to="/"
              data-testid="privacy-back-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors hover:text-clay"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              Back to home
            </Link>
            <h1 className="mt-8 font-serif text-5xl font-medium tracking-tighter text-ink sm:text-6xl">
              Privacy <em className="text-pine">Policy.</em>
            </h1>
            <p className="mt-4 text-sm text-zinc-500">Last updated: August 2026</p>
            <p className="mt-8 text-base leading-relaxed text-zinc-600 sm:text-lg">
              CalCor Solutions is a small firm, and our approach to your information is equally
              straightforward: collect the minimum, use it only to serve you, protect it carefully,
              and never sell it. The details below explain exactly how that works on this website.
            </p>
          </FadeUp>

          <div className="mt-14 space-y-12">
            {SECTIONS.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.05}>
                <section data-testid={`privacy-section-${i + 1}`}>
                  <h2 className="font-serif text-2xl tracking-tight text-ink">
                    <span className="mr-3 font-serif italic text-pine/40">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.title}
                  </h2>
                  <div className="mt-4 space-y-4 border-l border-black/10 pl-6 text-sm leading-relaxed text-zinc-600 sm:text-base">
                    {s.body.map((p) => (
                      <p key={p.slice(0, 40)}>{p}</p>
                    ))}
                  </div>
                </section>
              </FadeUp>
            ))}

            <FadeUp>
              <section data-testid="privacy-contact" className="rounded-3xl border border-black/10 bg-white p-9">
                <h2 className="font-serif text-2xl tracking-tight text-ink">Questions about privacy?</h2>
                <p className="mt-4 text-sm leading-relaxed text-zinc-600 sm:text-base">
                  Email us at{" "}
                  <a
                    href={`mailto:${CONTACT.email}`}
                    data-testid="privacy-email-link"
                    className="font-semibold text-pine transition-colors hover:text-clay"
                  >
                    {CONTACT.email}
                  </a>{" "}
                  or call{" "}
                  <a
                    href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                    data-testid="privacy-phone-link"
                    className="font-semibold text-pine transition-colors hover:text-clay"
                  >
                    {CONTACT.phone}
                  </a>
                  . We are glad to explain anything on this page in plain language.
                </p>
              </section>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
